function normalizeIdentity(value) {
    return String(value || '').trim().toLowerCase();
}

function startOfDay(value) {
    const date = value instanceof Date ? new Date(value) : new Date(value || Date.now());
    date.setHours(0, 0, 0, 0);
    return date;
}

const MANUAL_ACTION_TYPE_LABELS = {
    project_specific: 'Project Specific',
    business_development: 'Business Development',
    internal_admin: 'Internal Admin',
    other: 'Other'
};

export function getActionStatus(action, now = new Date()) {
    if (action?.completed_at) return 'completed';
    if (!action?.due_at) return 'open';
    const due = startOfDay(action.due_at);
    const today = startOfDay(now);
    const days = Math.round((due.getTime() - today.getTime()) / 86400000);
    if (days < 0) return 'overdue';
    if (days === 0) return 'due_today';
    if (days <= 7) return 'due_soon';
    return 'on_track';
}

export function buildActionRepository({ slaItems = [], actionState = {}, authUser = {} } = {}) {
    const currentUserKey = normalizeIdentity(actionState?.current_user_key || authUser?.username || authUser?.email || authUser?.user_uuid);
    const currentName = String(authUser?.display_name || authUser?.displayName || authUser?.username || '').trim();
    const users = Array.isArray(actionState?.users) ? actionState.users : [];
    const directory = [
        ...users,
        { key: currentUserKey, name: currentName, email: authUser?.email || authUser?.username || '' }
    ].filter((person) => normalizeIdentity(person?.key));
    const keyByName = new Map(directory.map((person) => [normalizeIdentity(person?.name), normalizeIdentity(person?.key)]));
    const nameByKey = new Map(directory.map((person) => [normalizeIdentity(person?.key), String(person?.name || person?.email || '').trim()]));
    const stateByKey = new Map((Array.isArray(actionState?.actions) ? actionState.actions : []).map((entry) => [entry.key, entry]));

    const systemActions = (Array.isArray(slaItems) ? slaItems : [])
        .filter((item) => item?.action_active !== false)
        .map((item) => {
            const key = String(item?.action_key || '').trim()
                || `${String(item?.reference || '').trim()}::${String(item?.report_instance || '').trim()}`;
            const state = stateByKey.get(key) || {};
            const delegation = state.delegation && typeof state.delegation === 'object' ? state.delegation : null;
            const leadName = String(item?.lead_surveyor || '').trim();
            const ownerKey = normalizeIdentity(delegation?.to_key || keyByName.get(normalizeIdentity(leadName)) || '');
            const ownerName = String(delegation?.to_name || nameByKey.get(ownerKey) || leadName || 'Unassigned').trim();
            const priorityKeys = Array.isArray(state.priority_user_keys) ? state.priority_user_keys.map(normalizeIdentity) : [];
            const dismissedKeys = Array.isArray(state.dismissed_user_keys) ? state.dismissed_user_keys.map(normalizeIdentity) : [];
            const completedKeys = Array.isArray(state.completed_user_keys) ? state.completed_user_keys.map(normalizeIdentity) : [];
            const workflowCompleted = completedKeys.includes(ownerKey);
            const reportType = String(item?.type || '').trim().toLowerCase();
            const isBookingAction = item?.action_type === 'book_site_visit' || reportType === 'site_visit_booking';
            const isConfirmLiveAction = item?.action_type === 'confirm_live_projects' || reportType === 'confirm_live_projects';
            const interimNumber = Number(item?.interim_number);
            const reportLabel = String(item?.report_instance || '').startsWith('interim:')
                ? `Interim Report${Number.isFinite(interimNumber) && interimNumber > 0 ? ` ${interimNumber}` : ''}`
                : 'Initial Report';

            return {
                ...item,
                key,
                action_id: key,
                source: 'system',
                direct_completion: false,
                title: isConfirmLiveAction
                    ? 'Confirm Live Projects'
                    : isBookingAction
                        ? `${String(item?.reference || '').trim()} · Book ${reportLabel} Site Visit`
                        : `${String(item?.reference || '').trim()} · ${reportLabel}`,
                description: isConfirmLiveAction
                    ? `${Array.isArray(item?.affected_projects) ? item.affected_projects.length : 0} project${item?.affected_projects?.length === 1 ? '' : 's'} awaiting confirmation`
                    : String(item?.project_name || '').trim(),
                detail_type: isConfirmLiveAction ? 'Project Status' : (isBookingAction ? 'Site Visit' : reportLabel),
                detail_text: isConfirmLiveAction
                    ? 'Review projects with an issued Initial Report that have not yet progressed to an Interim Site Visit.'
                    : isBookingAction
                        ? `Book the ${reportLabel.toLowerCase()} Site Visit.`
                        : (reportType === 'interim'
                            ? 'Prepare and issue the scheduled monitoring report.'
                            : 'Prepare and issue the initial monitoring report.'),
                owner_key: ownerKey,
                owner_name: ownerName,
                priority_flag: priorityKeys.includes(ownerKey) || (ownerKey === currentUserKey && Boolean(state.priority)),
                dismissed: dismissedKeys.includes(ownerKey) || (ownerKey === currentUserKey && Boolean(state.dismissed)),
                source_completed: Boolean(item?.completed_at),
                workflow_completed: workflowCompleted,
                delegated_by: delegation && ownerKey === currentUserKey ? String(delegation.from_name || '').trim() : '',
                status: item?.completed_at || workflowCompleted
                    ? 'completed'
                    : (item?.sla_paused ? 'paused' : getActionStatus(item))
            };
        });

    const manualActions = (Array.isArray(actionState?.manual_actions) ? actionState.manual_actions : []).map((item) => {
        const delegation = item?.delegation && typeof item.delegation === 'object' ? item.delegation : null;
        const ownerKey = normalizeIdentity(delegation?.to_key || item?.owner_key);
        const priorityKeys = Array.isArray(item?.priority_users) ? item.priority_users.map(normalizeIdentity) : [];
        const dismissedKeys = Array.isArray(item?.dismissed_users) ? item.dismissed_users.map(normalizeIdentity) : [];
        const isReportUploadAction = item?.system_generated === true && item?.action_type === 'upload_report';
        return {
            ...item,
            target_report_instance: isReportUploadAction ? String(item?.report_instance || '').trim() : '',
            reference: 'manual',
            report_instance: String(item?.id || '').trim(),
            action_id: String(item?.id || '').trim(),
            source: isReportUploadAction ? 'system' : 'manual',
            direct_completion: true,
            source_completed: isReportUploadAction && item?.completion_source === 'report_upload',
            detail_type: isReportUploadAction ? 'Upload Report PDF' : (MANUAL_ACTION_TYPE_LABELS[String(item?.classification || '').trim().toLowerCase()] || 'Personal Action'),
            detail_text: isReportUploadAction ? 'Upload the completed report to the Information Portal.' : String(item?.related_name || item?.custom_note || 'User-created action').trim(),
            owner_key: ownerKey,
            owner_name: String(delegation?.to_name || item?.owner_name || nameByKey.get(ownerKey) || 'Unassigned').trim(),
            priority_flag: priorityKeys.includes(ownerKey) || (ownerKey === currentUserKey && Boolean(item?.priority)),
            dismissed: dismissedKeys.includes(ownerKey) || (ownerKey === currentUserKey && Boolean(item?.dismissed)),
            delegated_by: delegation && ownerKey === currentUserKey ? String(delegation.from_name || '').trim() : '',
            status: getActionStatus(item)
        };
    });

    const allActions = [...manualActions, ...systemActions];
    const visibleActions = allActions.filter((action) => !action.dismissed);
    return {
        currentUserKey,
        users,
        allActions: visibleActions,
        myActions: visibleActions.filter((action) => action.owner_key === currentUserKey),
        teamActions: visibleActions.filter((action) => action.owner_key !== currentUserKey)
    };
}

export function isActionInCurrentWeek(action, now = new Date()) {
    if (!action?.due_at) return false;
    const today = startOfDay(now);
    const day = today.getDay() || 7;
    const weekStart = new Date(today);
    weekStart.setDate(today.getDate() - day + 1);
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekStart.getDate() + 7);
    const due = new Date(action.due_at);
    return due >= weekStart && due < weekEnd;
}